
import { GoogleGenAI, Type } from "@google/genai";
import { collection, addDoc, serverTimestamp, doc, updateDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { ProgressPhoto, AIAnalysis, User } from '../types';
import { awardXP } from './gamificationService';

export const analyzeProgressPhoto = async (base64Image: string): Promise<AIAnalysis> => {
  try {
    // Initializing GoogleGenAI inside the function to use the correct API key from process.env.API_KEY.
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: 'image/jpeg',
              data: base64Image.split(',')[1] || base64Image,
            },
          },
          {
            text: `Analyze this body photo for fitness tracking. Provide:
            1. Estimated body fat percentage (e.g. "18-22%")
            2. Muscle definition level (1-10)
            3. Posture assessment
            4. Visible muscle groups
            5. Suggestions for improvement
            6. Encouragement
            Output in valid JSON format.`,
          },
        ],
      },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            bodyFatPercentage: { type: Type.STRING },
            muscleDefinition: { type: Type.INTEGER },
            posture: { type: Type.STRING },
            visibleMuscles: { type: Type.ARRAY, items: { type: Type.STRING } },
            suggestions: { type: Type.ARRAY, items: { type: Type.STRING } },
            encouragement: { type: Type.STRING },
          },
          required: ["bodyFatPercentage", "muscleDefinition", "posture", "visibleMuscles", "suggestions", "encouragement"],
        },
      },
    });

    const result = JSON.parse(response.text || '{}');
    return {
      ...result,
      processedAt: new Date(),
    };
  } catch (error) {
    console.error("AI Analysis error:", error);
    throw error;
  }
};

export const saveProgressPhoto = async (user: User, base64Image: string, data: Partial<ProgressPhoto>) => {
  const photosRef = collection(db, "users", user.id, "progressPhotos");
  
  // 1. Analisi AI (se abilitata dal piano o se admin)
  let aiResults = null;
  if (user.plan === 'unlimited' || user.role === 'admin') {
    aiResults = await analyzeProgressPhoto(base64Image);
  }

  // 2. Salvataggio in Firestore
  const newPhoto = {
    userId: user.id,
    imageUrl: base64Image, // In produzione caricheremmo su Storage
    uploadedAt: serverTimestamp(),
    weight: data.weight || user.weight,
    notes: data.notes || "",
    privacy: data.privacy || "private",
    aiAnalysis: aiResults,
    measurements: data.measurements || {}
  };

  const docRef = await addDoc(photosRef, newPhoto);
  
  // 3. Award XP
  await awardXP(user.id, 50, "Progress Photo Upload");
  
  return { id: docRef.id, ...newPhoto };
};

export const fetchUserPhotos = async (userId: string): Promise<ProgressPhoto[]> => {
  const q = query(
    collection(db, "users", userId, "progressPhotos"),
    orderBy("uploadedAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ProgressPhoto));
};
